/**
 * UsdtWithdraw — вывод USDT с кастодиального кошелька (Flow step="usdt_withdraw"). Без KYC.
 * Ввод адреса (вставка из буфера, скан QR, адресная книга/история), комиссия 3 USDT, нельзя
 * отправить всё — максимум = баланс − комиссия. По «Отправить» → он-чейн отправка на сервере
 * (энергия/bandwidth + перевод получателю + перевод комиссии на горячий кошелёк). Пропсы:
 * onNext(withdrawal), onBack.
 */
import { useCallback, useEffect, useState } from "react";
import { tg } from "@/telegram/tg";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Field } from "@/components/Field";
import { MaxButton } from "@/components/MaxButton";
import { Note } from "@/components/Note";
import { Spinner } from "@/components/Spinner";
import {
  ApiError,
  friendlyError,
  usdtWithdrawApi,
  type CustodialWithdrawal,
  type UsdtWithdrawConfig,
  type WithdrawAddress,
} from "@/lib/api";
import { formatUsdt, maskMiddle, validateTrc20 } from "@/lib/format";

type Props = { onNext: (w: CustodialWithdrawal) => void; onBack: () => void };

const TRC20 = /T[1-9A-HJ-NP-Za-km-z]{33}/;

function extractAddress(raw: string): string {
  // QR/буфер может содержать "tron:TXXX?amount=…" или просто адрес — вытаскиваем T-адрес
  const m = raw.match(TRC20);
  return m ? m[0] : raw.trim();
}

export function UsdtWithdraw({ onNext, onBack }: Props) {
  useBackButton(onBack);
  const [cfg, setCfg] = useState<UsdtWithdrawConfig | null>(null);
  const [book, setBook] = useState<WithdrawAddress[]>([]);
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, a] = await Promise.all([usdtWithdrawApi.config(), usdtWithdrawApi.addresses()]);
        if (!alive) return;
        setCfg(c);
        setBook(a.addresses);
      } catch {
        /* экран покажет нули */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const fee = cfg?.fee_usdt ?? 3;
  const max = cfg?.max_usdt ?? 0;
  const amountNum = parseFloat(amount.replace(",", ".")) || 0;

  const addrValid = validateTrc20(address);
  const amountValid = amountNum > 0 && amountNum <= max;
  const canSubmit = addrValid && amountValid && !submitting && !!cfg;

  const setMax = useCallback(() => setAmount(max > 0 ? String(max) : "0"), [max]);

  const paste = useCallback(async () => {
    setError(null);
    const wa = tg();
    if (wa?.readTextFromClipboard) {
      wa.readTextFromClipboard((text) => text && setAddress(extractAddress(text)));
      return;
    }
    try {
      const text = await navigator.clipboard.readText();
      if (text) setAddress(extractAddress(text));
    } catch {
      setError("Не удалось прочитать буфер обмена");
    }
  }, []);

  const scan = useCallback(() => {
    const wa = tg();
    if (!wa?.showScanQrPopup) {
      setError("Сканирование доступно только в приложении Telegram");
      return;
    }
    wa.showScanQrPopup({ text: "Наведите на QR-код адреса" }, (text) => {
      const addr = extractAddress(text);
      if (validateTrc20(addr)) {
        setAddress(addr);
        wa.closeScanQrPopup?.();
        return true; // закрыть поповер
      }
      return false;
    });
  }, []);

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const w = await usdtWithdrawApi.create(address.trim(), amountNum);
      onNext(w);
    } catch (e) {
      setError(e instanceof ApiError ? friendlyError(e) : "Не удалось создать вывод");
      setSubmitting(false);
    }
  }, [canSubmit, address, amountNum, onNext]);

  useMainButton({
    text: submitting ? "Отправляем…" : "Отправить",
    onClick: submit,
    active: canSubmit,
    loading: submitting,
  });

  // USDT на Tron — пользователю показываем стандарт сети (TRC20), а не chain-env (mainnet/nile)
  const net = "TRC20";

  return (
    <Screen title="Вывести USDT" subtitle={`Сеть ${net}. Комиссия ${fee} USDT удерживается сверх суммы.`}>
      {!cfg ? (
        <div className="grid place-items-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Баланс / доступно */}
          <div className="rounded-2xl border border-white/[0.06] bg-surface-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-[13px] text-white/45">Доступно к выводу</span>
              <span className="text-[15px] font-semibold tabular-nums">{formatUsdt(max)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-[12px] text-white/35">
              <span>Баланс {formatUsdt(cfg.balance_usdt)}</span>
              <span>комиссия {fee} USDT</span>
            </div>
          </div>

          {/* Адрес + вставка/скан */}
          <div>
            <Field
              label="Адрес получателя"
              placeholder={`T… (${net})`}
              value={address}
              onChange={(e) => setAddress(e.target.value.trim())}
              error={address && !addrValid ? "Неверный адрес TRC20" : undefined}
            />
            <div className="mt-2 flex gap-2">
              <SmallBtn onClick={paste}>📋 Вставить</SmallBtn>
              <SmallBtn onClick={scan}>⛶ Скан QR</SmallBtn>
            </div>
          </div>

          {/* Адресная книга / история */}
          {book.length > 0 && (
            <div>
              <div className="mb-1.5 text-[13px] text-white/45">Недавние адреса</div>
              <div className="space-y-1.5">
                {book.slice(0, 6).map((a) => (
                  <button
                    key={a.address}
                    onClick={() => setAddress(a.address)}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ring-1 ring-inset transition ${
                      address === a.address ? "bg-brand/15 ring-brand/40" : "bg-white/[0.03] ring-white/[0.05] active:bg-white/[0.06]"
                    }`}
                  >
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white/[0.06] text-[12px]">₮</span>
                    <span className="min-w-0 flex-1">
                      {a.label && <span className="block truncate text-[13px] font-medium">{a.label}</span>}
                      <span className="block truncate font-mono text-[12px] text-white/55">{maskMiddle(a.address, 8, 8)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Сумма + Макс */}
          <div>
            <Field
              label="Сумма USDT"
              inputMode="decimal"
              placeholder="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
              error={amount && !amountValid ? `Максимум ${formatUsdt(max)}` : undefined}
              suffix={<MaxButton onClick={setMax} disabled={max <= 0} />}
            />
          </div>

          <Note variant="warning">
            Отправляйте только на адрес в сети <b>{net}</b>. Комиссия <b>{fee} USDT</b> удерживается дополнительно —
            отправить весь баланс нельзя, оставьте место под комиссию.
          </Note>

          {error && <Note variant="danger">{error}</Note>}

          <FallbackButton text={submitting ? "Отправляем…" : "Отправить"} onClick={submit} disabled={!canSubmit} loading={submitting} />
        </div>
      )}
    </Screen>
  );
}

function SmallBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex-1 rounded-xl border border-white/[0.08] bg-white/[0.03] py-2.5 text-[13px] font-medium text-white/80 active:bg-white/[0.07]"
    >
      {children}
    </button>
  );
}
